// Reading the signed-in user's roles for an access guard.
//
// A guard has to tell two very different things apart: "this person is not an
// admin" and "we could not find out". Every guard in the app used to discard
// the query's error, so a failed lookup returned no roles and the person was
// shown "Access Denied" and sent to the parent dashboard. On a phone that had
// been asleep, or a page whose requests were cut short by the stale-chunk
// reload after a deploy, an admin was told they were not one — and because
// nothing threw, Sentry never heard about it.
//
// This helper answers with `failed` when the check could not be completed.
// Callers must never read that as a refusal.
import * as Sentry from "@sentry/react";
import { isAuthApiError, isAuthSessionMissingError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

/**
 * The auth server saying this device's sign-in no longer exists - as opposed
 * to not answering. A session the server has ended (expired, signed out
 * elsewhere, refresh token gone) stays in the phone's storage, so the app
 * still thinks it is signed in, every request goes out with a dead token and
 * every guard reports that it could not check.
 *
 * 14 Sep: a parent's iPhone did exactly that for six days, 22 reports, every
 * page she opened offering "try again" when trying again could never work.
 * Only a new sign-in helps, so that is where she is sent.
 */
const DEAD_SESSION_CODES = new Set([
  "session_not_found", "session_expired", "refresh_token_not_found",
  "refresh_token_already_used", "bad_jwt", "user_not_found",
]);

function sessionIsGone(error: unknown): boolean {
  if (isAuthSessionMissingError(error)) return true;
  if (!isAuthApiError(error)) return false; // a network failure is not an answer
  return DEAD_SESSION_CODES.has(error.code ?? "") || error.status === 401 || error.status === 403;
}

/** Pages that need a signed-in person. The header reads roles on the public
 *  website too, and someone reading the programs page with a stale sign-in
 *  should simply be signed out there, not sent to a login form. */
const PORTAL_PREFIXES = [
  "/admin", "/teacher-dashboard", "/parent-dashboard", "/boss", "/settings", "/manual",
  "/forms", "/report-injury", "/print", "/documents",
];
const onPortalPage = () =>
  PORTAL_PREFIXES.some((p) => window.location.pathname === p || window.location.pathname.startsWith(`${p}/`));

/** Clear the dead session from this device and, on a portal page, send the
 *  person to sign in, keeping the page they were on so they come straight
 *  back to it. */
async function leaveDeadSession(error: { code?: string; status?: number }) {
  Sentry.addBreadcrumb({
    category: "auth",
    level: "info",
    message: "Role guard: the session had ended on the server; signing this device out",
    data: { code: error.code, status: error.status },
  });
  const sendToSignIn = onPortalPage();
  if (sendToSignIn) {
    try {
      sessionStorage.setItem("redirectAfterLogin", `${window.location.pathname}${window.location.search}`);
    } catch { /* storage refused: they land on their dashboard instead */ }
  }
  await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
  if (sendToSignIn) window.location.replace("/auth?expired=1");
}

/**
 * Report a failed guard under a name of our own.
 *
 * Sentry's ignoreErrors list deliberately drops "TypeError: Failed to fetch"
 * and Safari's "TypeError: Load failed" as everyday network noise, and it
 * matches on substrings — so reporting the underlying error, or quoting its
 * message in ours, would be thrown away silently. Being locked out of the
 * admin dashboard is not noise, so the cause travels in the event's data
 * where no filter reads it.
 */
function reportGuardFailure(error: { message?: string; code?: string; details?: string; hint?: string }, userId: string) {
  Sentry.captureException(new Error("Role guard: the roles lookup failed"), {
    tags: { area: "role-guard" },
    extra: {
      userId,
      cause: error?.message ?? "(no message)",
      code: error?.code,
      details: error?.details,
      hint: error?.hint,
      online: typeof navigator === "undefined" ? undefined : navigator.onLine,
    },
  });
}

export interface RoleCheck {
  roles: string[];
  /** The lookup could not be completed. Not the same as having no roles. */
  failed: boolean;
  /** The sign-in had ended on the server; the device is being sent to sign
   *  in. Also `failed`, so no caller mistakes it for a refusal. */
  signedOut?: boolean;
}

export async function fetchRolesForGuard(userId: string): Promise<RoleCheck> {
  const read = () => supabase.from("user_roles").select("role").eq("user_id", userId);

  let { data, error } = await read();

  if (error) {
    // An expired access token is the usual cause on a phone returning from
    // the background: the refresh had not landed before the guard asked.
    // Refresh once and ask again before doubting the person.
    let refreshError: unknown = null;
    try {
      ({ error: refreshError } = await supabase.auth.refreshSession());
    } catch (e) {
      refreshError = e;
    }
    if (refreshError && sessionIsGone(refreshError)) {
      await leaveDeadSession(refreshError as { code?: string; status?: number });
      return { roles: [], failed: true, signedOut: true };
    }
    ({ data, error } = await read());
  }

  if (error) {
    reportGuardFailure(error, userId);
    return { roles: [], failed: true };
  }

  const roles = (data ?? []).map((r) => r.role as string);

  // An empty list is the dangerous answer. Every policy on user_roles is
  // granted to `authenticated` and keyed on auth.uid(), so a request that
  // reaches PostgREST without a valid token matches no policy and comes back
  // as an empty array with HTTP 200 and no error at all — identical to a real
  // account that has no roles yet. Before believing it, make the auth server
  // vouch for the token: getUser() validates it over the network, unlike
  // getSession(), which only reads local storage and will happily hand back
  // an expired session.
  if (roles.length === 0) {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError && sessionIsGone(userError)) {
      await leaveDeadSession(userError);
      return { roles: [], failed: true, signedOut: true };
    }
    if (userError || !userData?.user) {
      reportGuardFailure(
        { message: userError?.message ?? "getUser returned no user", code: "empty-roles-unverified" },
        userId,
      );
      return { roles: [], failed: true };
    }
  }

  return { roles, failed: false };
}
